const API_KEY = "AIzaSyBUK6J5FjHta2nM0ZDCzuXc3HNURA8eXTU";
const API_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent";

class GeminiChatService {
  constructor() {
    this.chatHistory = [];
  }

  // Clear chat history
  clearChatHistory() {
    this.chatHistory = [];
    console.log('🧹 Chat history cleared');
  }

  // Test connection
  async testConnection() {
    try {
      const response = await fetch('https://www.google.com');
      if (!response.ok) return false;
    } catch (error) {
      return false;
    }

    const testURL = `https://generativelanguage.googleapis.com/v1beta/models?key=${API_KEY}`;
    try {
      const response = await fetch(testURL);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  // Generate creative genre suggestions
  async generateCreativeGenreSuggestions(songContext = null, currentGenres = [], selectedGenres = []) {
    let prompt;

    if (selectedGenres.length > 0) {
      const lastSelectedGenre = selectedGenres[selectedGenres.length - 1];
      prompt = `Generate 8 related sub-genres and variations for '${lastSelectedGenre}' music. `;

      switch (lastSelectedGenre.toLowerCase()) {
        case 'hip-hop':
        case 'hip hop':
          prompt += 'Include variations like boom bap, trap, conscious rap, drill, old school, east coast, west coast, etc.';
          break;
        case 'rock':
          prompt += 'Include variations like indie rock, punk rock, alternative rock, prog rock, classic rock, garage rock, etc.';
          break;
        case 'pop':
          prompt += 'Include variations like indie pop, synth pop, dream pop, electropop, k-pop, art pop, etc.';
          break;
        case 'electronic':
          prompt += 'Include variations like house, techno, ambient, drum & bass, trance, dubstep, etc.';
          break;
        case 'r&b':
        case 'rnb':
          prompt += 'Include variations like neo soul, contemporary r&b, alternative r&b, funk, motown, etc.';
          break;
        default:
          prompt += 'Include specific sub-genres, regional variations, and closely related styles.';
      }
    } else {
      prompt = 'Generate 8 creative and diverse music genre suggestions for a user who wants to change the genre of their song.';

      if (songContext) {
        prompt += ` The current song is about: ${songContext}.`;
      }

      if (currentGenres.length > 0) {
        prompt += ` Current genres are: ${currentGenres.join(', ')}.`;
      }

      prompt += ' Include a mix of mainstream genres (pop, rock, hip-hop) and creative/unique genres (synthwave, lo-fi, afrobeat, indie folk, etc.).';
    }

    prompt += " Format each genre with a '+' prefix like '+boom bap', '+synthwave'. Respond with ONLY a comma-separated list of 8 genres with + prefixes, no explanations or extra text.";

    const url = `${API_URL}?key=${API_KEY}`;
    const payload = {
      contents: [{
        role: 'user',
        parts: [{ text: prompt }]
      }],
      generationConfig: {
        responseMimeType: 'text/plain',
        temperature: 0.9,
        maxOutputTokens: 100
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to get genre suggestions');
      }

      const data = await response.json();
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (text) {
        const genres = text.trim()
          .split(',')
          .map(genre => {
            const trimmed = genre.trim().toLowerCase();
            const cleanGenre = trimmed.startsWith('+') ? trimmed.slice(1) : trimmed;
            return `+${cleanGenre}`;
          })
          .filter(g => g.length > 1 && !selectedGenres.includes(g))
          .slice(0, 8);

        return genres;
      }

      throw new Error('Failed to parse genre suggestions');
    } catch (error) {
      console.error('Genre suggestion error:', error);

      // Fallback genres
      const fallbackGenres = selectedGenres.length > 0
        ? this.getContextualFallbackGenres(selectedGenres[selectedGenres.length - 1])
        : ['+synthwave', '+lo-fi', '+indie folk', '+dream pop', '+tropical house', '+garage rock', '+neo soul', '+chill hop'];

      return fallbackGenres.filter(g => !selectedGenres.includes(g)).slice(0, 8);
    }
  }

  getContextualFallbackGenres(lastGenre) {
    const fallbackMap = {
      'hip-hop': ['+boom bap', '+trap', '+conscious rap', '+drill', '+old school', '+east coast', '+west coast', '+chicago rap'],
      'hip hop': ['+boom bap', '+trap', '+conscious rap', '+drill', '+old school', '+east coast', '+west coast', '+chicago rap'],
      'rock': ['+indie rock', '+punk rock', '+alt rock', '+prog rock', '+classic rock', '+garage rock', '+post rock', '+math rock'],
      'pop': ['+indie pop', '+synth pop', '+dream pop', '+electropop', '+k-pop', '+art pop', '+bedroom pop', '+hyperpop'],
      'electronic': ['+house', '+techno', '+ambient', '+drum & bass', '+trance', '+dubstep', '+future bass', '+breakbeat'],
      'r&b': ['+neo soul', '+contemporary r&b', '+alt r&b', '+funk', '+motown', '+quiet storm', '+new jack swing', '+smooth jazz'],
      'rnb': ['+neo soul', '+contemporary r&b', '+alt r&b', '+funk', '+motown', '+quiet storm', '+new jack swing', '+smooth jazz']
    };

    return fallbackMap[lastGenre.toLowerCase()] || ['+indie', '+alternative', '+experimental', '+fusion', '+neo', '+contemporary', '+underground', '+crossover'];
  }

  // Check if message is an extend request
  isExtendRequest(message) {
    const lowercased = message.toLowerCase();
    return lowercased.includes('extend') && lowercased.includes('after');
  }

  // Check if message is a lyrics editing request
  isLyricsEditingRequest(message) {
    const lowercased = message.toLowerCase();
    return lowercased.includes('create a new version') && lowercased.includes('with the following lyrics:');
  }

  // Check if message is a genre change request
  isGenreChangeRequest(message) {
    const lowercased = message.toLowerCase();
    return lowercased.includes('- change the genre to') || lowercased.includes('change the genre to');
  }

  // Check if message is instrumental extension
  isInstrumentalExtensionRequest(message) {
    const lowercased = message.toLowerCase();
    const instrumentalKeywords = ['instrumental', 'no lyrics', 'just music', 'music only', 'without lyrics', 'no words', 'beat only', 'track only'];
    return instrumentalKeywords.some(keyword => lowercased.includes(keyword));
  }

  // Check if message is lyrics extension
  isLyricsExtensionRequest(message) {
    const lowercased = message.toLowerCase();

    if (this.isInstrumentalExtensionRequest(message)) {
      return false;
    }

    const lyricsKeywords = ['add lyrics', 'with lyrics', 'lyrics extension', 'extend with lyrics', 'more verses', 'additional lyrics', 'continue the song', 'add more lyrics'];
    const hasLyricsKeyword = lyricsKeywords.some(keyword => lowercased.includes(keyword));

    const hasMultipleLines = message.split('\n').length > 2;
    const hasLyricsStructure = lowercased.includes('[verse') || lowercased.includes('[chorus') || lowercased.includes('[bridge');

    const isGeneralExtension = lowercased.includes('extend') || lowercased.includes('continue') || lowercased.includes('add more') || lowercased.includes('longer');

    return hasLyricsKeyword || hasLyricsStructure || (hasMultipleLines && !this.isInstrumentalExtensionRequest(message)) || isGeneralExtension;
  }

  // Check if message is specific section request
  isSpecificSectionRequest(message) {
    const lowercased = message.toLowerCase();
    const sectionKeywords = [
      '3rd verse', 'third verse', 'add verse', 'new verse', 'verse 3',
      'bridge', 'add bridge', 'new bridge',
      'chorus', 'add chorus', 'new chorus',
      'outro', 'add outro', 'ending',
      'pre-chorus', 'add pre-chorus'
    ];

    return sectionKeywords.some(keyword => lowercased.includes(keyword));
  }

  // Check if message is custom song creation
  isCustomSongCreationRequest(message) {
    const lowercased = message.toLowerCase();
    const customSongPatterns = [
      'can you create custom a song:',
      'create custom song:',
      'custom song:',
      'make custom song:',
      'generate custom song:'
    ];

    const hasCustomPattern = customSongPatterns.some(pattern => lowercased.includes(pattern));
    return hasCustomPattern && (lowercased.includes('lyrics:') || lowercased.includes('style:') || lowercased.includes('genre:'));
  }

  // Parse lyrics from text
  parseLyricsFromText(text) {
    const sections = [];
    const lines = text.split('\n');
    let currentSection = null;
    let currentContent = [];

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('[') && trimmedLine.endsWith(']')) {
        if (currentSection && currentContent.length > 0) {
          sections.push({
            type: currentSection,
            content: currentContent.join('\n')
          });
        }

        currentSection = trimmedLine.slice(1, -1);
        currentContent = [];
      } else if (trimmedLine) {
        currentContent.push(trimmedLine);
      }
    }

    if (currentSection && currentContent.length > 0) {
      sections.push({
        type: currentSection,
        content: currentContent.join('\n')
      });
    }

    if (sections.length === 0 && text.trim()) {
      sections.push({
        type: 'Lyrics',
        content: text.trim()
      });
    }

    return { sections };
  }

  // Extract base title from versioned title
  extractBaseTitleFromVersionedTitle(title) {
    const match = title.match(/^(.+?)\s*\(#\d+\)$/);
    return match ? match[1] : title;
  }

  // Get current song from context
  getCurrentRemixBaseTitleFromContext() {
    for (let i = this.chatHistory.length - 1; i >= 0; i--) {
      const item = this.chatHistory[i];
      if (item.role === 'model') {
        try {
          const content = item.parts[0].text;
          const songResponse = JSON.parse(content);
          if (songResponse.songs && songResponse.songs.length > 0) {
            return songResponse.songs[0].title;
          }
        } catch (e) {
          continue;
        }
      }
    }
    return 'Song Title';
  }

  // Get current lyrics from context
  getCurrentLyricsFromContext() {
    for (let i = this.chatHistory.length - 1; i >= 0; i--) {
      const item = this.chatHistory[i];
      if (item.role === 'model') {
        try {
          const content = item.parts[0].text;
          const songResponse = JSON.parse(content);
          if (songResponse.lyrics) {
            return songResponse.lyrics;
          }
        } catch (e) {
          continue;
        }
      }
    }
    return null;
  }

  // Generate responses for different request types
  generateExtendResponse(message) {
    let songTitle = 'the song';
    let timestamp = 'that point';

    const extendMatch = message.match(/Extend (.+?) after/i);
    if (extendMatch) {
      songTitle = extendMatch[1];
    }

    const afterMatch = message.match(/after (.+)$/i);
    if (afterMatch) {
      timestamp = afterMatch[1].trim();
    }

    return `Ok got it, I'll extend ${songTitle} starting from ${timestamp}. Before I proceed, let me know if you want to add lyrics to your extension or the vibe of your instrumental.`;
  }

  generateGenreChangeResponse(message) {
    let genres = [];
    let songTitle = 'the song';

    const dashMatch = message.match(/^(.+?)\s*-\s*change the genre to/i);
    if (dashMatch) {
      songTitle = dashMatch[1].trim();
    }

    const genreMatch = message.match(/change the genre to (.+)$/i);
    if (genreMatch) {
      genres = genreMatch[1].split(',').map(g => g.trim()).filter(g => g);
    }

    if (songTitle === 'the song') {
      songTitle = this.getCurrentRemixBaseTitleFromContext();
    }

    const baseTitleForRemix = this.extractBaseTitleFromVersionedTitle(songTitle);
    const remixTitle = `${baseTitleForRemix} Remix`;

    const songResponse = {
      reply: `I've remixed ${songTitle} in ${genres.join(' and ')} style! Here are two versions:`,
      lyrics: null,
      songs: [
        { title: `${remixTitle} (#1)`, genres, audioURL: 'song1' },
        { title: `${remixTitle} (#2)`, genres, audioURL: 'song2' }
      ],
      suggestions: ['Change genre', 'Edit lyrics', 'Extend', 'Replace section']
    };

    return JSON.stringify(songResponse);
  }

  generateInstrumentalExtensionResponse(message) {
    const currentSong = this.getCurrentRemixBaseTitleFromContext();
    const baseTitleForExtension = this.extractBaseTitleFromVersionedTitle(currentSong);
    const extendedTitle = `${baseTitleForExtension} Extended`;

    const songResponse = {
      reply: "Here's your extended version, I've created two versions for you to listen to",
      lyrics: null,
      songs: [
        { title: `${extendedTitle} (#1)`, genres: ['pop', 'indie'], audioURL: 'song1' },
        { title: `${extendedTitle} (#2)`, genres: ['pop', 'indie'], audioURL: 'song2' }
      ],
      suggestions: ['Change genre', 'Edit lyrics', 'Extend', 'Replace section']
    };

    return JSON.stringify(songResponse);
  }

  // Main send message function
  async sendMessage(userMessage, audioFileName = null, onStreamChunk = null) {
    // Add user message to history
    this.chatHistory.push({
      role: 'user',
      parts: [{ text: userMessage }]
    });

    // Check for custom song creation
    if (this.isCustomSongCreationRequest(userMessage)) {
      return this.generateCustomSongResponse(userMessage);
    }

    // Check for extend request
    if (this.isExtendRequest(userMessage)) {
      return this.generateExtendResponse(userMessage);
    }

    // Check for genre change
    if (this.isGenreChangeRequest(userMessage)) {
      return this.generateGenreChangeResponse(userMessage);
    }

    // Check for instrumental extension
    if (this.isInstrumentalExtensionRequest(userMessage)) {
      return this.generateInstrumentalExtensionResponse(userMessage);
    }

    // Try real API
    try {
      console.log('🌐 Attempting real API call...');
      const response = await this.sendMessageToAPI(userMessage);
      console.log('✅ Real API success!');

      // Add to history
      this.chatHistory.push({
        role: 'model',
        parts: [{ text: response }]
      });

      return response;
    } catch (error) {
      console.error('❌ Real API failed:', error);
      console.log('🚧 Falling back to mock data...');

      const mockResponse = await this.useMockResponse(userMessage);

      // Add to history
      this.chatHistory.push({
        role: 'model',
        parts: [{ text: mockResponse }]
      });

      return mockResponse;
    }
  }

  async sendMessageToAPI(userMessage) {
    const systemContext = this.getSystemContext();

    const contents = [
      {
        role: 'user',
        parts: [{ text: systemContext }]
      },
      ...this.chatHistory
    ];

    const payload = {
      contents,
      generationConfig: {
        responseMimeType: 'application/json',
        temperature: 0.9,
        maxOutputTokens: 2048
      }
    };

    const url = `${API_URL}?key=${API_KEY}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const aiResponse = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!aiResponse) {
      throw new Error('No response from API');
    }

    return aiResponse;
  }

  async useMockResponse(userMessage) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const topic = userMessage.includes('cat') ? 'your cat'
      : userMessage.includes('cafe') ? 'cafes'
      : userMessage.includes('friend') ? 'friendship'
      : 'your topic';

    const title = userMessage.includes('cat') ? 'Whiskers'
      : userMessage.includes('cafe') ? 'Cafe Dreams'
      : userMessage.includes('friend') ? 'Best Friend'
      : 'My Song';

    const mockResponse = {
      reply: `Here you go, I made two versions of the song about ${topic}. Let me know what you want to make edits, I can also create more versions.`,
      lyrics: {
        sections: [
          {
            type: 'Chorus',
            content: 'My little friend with whiskers bright\nCurled up cozy through the night\nPurring softly by my side\nIn your love I can confide'
          },
          {
            type: 'Verse 1',
            content: 'Morning sunbeams find you there\nStretching paws without a care\nChasing shadows, chasing dreams\nLife is better than it seems'
          }
        ]
      },
      songs: [
        { title: `${title} (#1)`, genres: ['indie', 'folk'], audioURL: 'song1' },
        { title: `${title} (#2)`, genres: ['indie', 'folk'], audioURL: 'song2' }
      ],
      suggestions: ['Create more', 'Extend', 'Make it faster', 'Add a bridge']
    };

    return JSON.stringify(mockResponse);
  }

  generateCustomSongResponse(message) {
    // Extract lyrics, style, genre from message
    let lyrics = '';
    let style = '';
    let genre = '';

    const lyricsMatch = message.match(/Lyrics:([\s\S]+?)(?:Style:|Genre:|Model Version:|$)/i);
    if (lyricsMatch) {
      lyrics = lyricsMatch[1].trim();
    }

    const styleMatch = message.match(/Style:([\s\S]+?)(?:Genre:|Model Version:|$)/i);
    if (styleMatch) {
      style = styleMatch[1].trim();
    }

    const genreMatch = message.match(/Genre:([\s\S]+?)(?:Model Version:|$)/i);
    if (genreMatch) {
      genre = genreMatch[1].trim();
    }

    const songTitle = this.generateSongTitleFromCustomContent(lyrics, style);
    const parsedLyrics = lyrics ? this.parseLyricsFromText(lyrics) : null;

    const genres = genre
      ? genre.split(',').map(g => g.trim().toLowerCase()).filter(g => g)
      : style
        ? this.extractGenresFromStyle(style)
        : ['pop', 'indie'];

    const replyMessage = lyrics && (genre || style)
      ? "Here you go! I've created your custom song with your lyrics and style. Here are two versions for you to listen to!"
      : lyrics
        ? "Perfect! I've created your custom song with your lyrics. Here are two versions for you to listen to!"
        : "Perfect! I've created your custom song. Here are two versions for you to listen to!";

    const songResponse = {
      reply: replyMessage,
      lyrics: parsedLyrics,
      songs: [
        { title: `${songTitle} (#1)`, genres, audioURL: 'song1' },
        { title: `${songTitle} (#2)`, genres, audioURL: 'song2' }
      ],
      suggestions: ['Create more', 'Edit lyrics', 'Change genre', 'Extend']
    };

    return JSON.stringify(songResponse);
  }

  generateSongTitleFromCustomContent(lyrics, style) {
    if (lyrics) {
      const words = lyrics.toLowerCase().split(/\s+/).filter(w => w.length > 2);
      const titleWords = ['love', 'heart', 'dream', 'night', 'light', 'time', 'world', 'life', 'home', 'fire', 'ocean', 'star', 'moon', 'sun', 'dance', 'song', 'music', 'hope', 'freedom', 'journey', 'magic', 'wonder', 'beautiful', 'strong', 'forever', 'together', 'memories', 'story', 'destiny', 'adventure'];

      for (const word of titleWords) {
        if (words.includes(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
      }

      const meaningfulWord = words.find(w => w.length > 3);
      if (meaningfulWord) {
        return meaningfulWord.charAt(0).toUpperCase() + meaningfulWord.slice(1);
      }
    }

    if (style) {
      const styleWords = style.toLowerCase().split(/\s+/).filter(w => w.length > 2 && !['style', 'genre', 'music', 'sound'].includes(w));
      if (styleWords.length > 0) {
        const word = styleWords[0];
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
    }

    const defaultTitles = ['Custom Song', 'My Song', 'New Track', 'Original', 'Creation'];
    return defaultTitles[Math.floor(Math.random() * defaultTitles.length)];
  }

  extractGenresFromStyle(style) {
    const lowercaseStyle = style.toLowerCase();
    const detectedGenres = [];

    const genreMap = {
      'pop': 'pop', 'rock': 'rock', 'hip-hop': 'hip-hop', 'hip hop': 'hip-hop',
      'rap': 'hip-hop', 'r&b': 'r&b', 'rnb': 'r&b', 'indie': 'indie',
      'electronic': 'electronic', 'edm': 'electronic', 'house': 'house',
      'techno': 'techno', 'jazz': 'jazz', 'blues': 'blues', 'folk': 'folk',
      'country': 'country', 'classical': 'classical', 'reggae': 'reggae',
      'metal': 'metal', 'punk': 'punk', 'funk': 'funk', 'disco': 'disco',
      'ambient': 'ambient', 'soul': 'soul', 'gospel': 'gospel',
      'latin': 'latin', 'acoustic': 'acoustic', 'alternative': 'alternative'
    };

    for (const [keyword, genre] of Object.entries(genreMap)) {
      if (lowercaseStyle.includes(keyword)) {
        detectedGenres.push(genre);
      }
    }

    const uniqueGenres = [...new Set(detectedGenres)].slice(0, 3);
    return uniqueGenres.length > 0 ? uniqueGenres : ['pop', 'indie'];
  }

  getSystemContext() {
    return `You make songs for Suno. At the moment you can support the following:
- Create songs - New song, cover, remix, alternate version, change genre
- Extend songs
- Edit Lyrics
- Edit Speed
- Change Genre
- Replace Section

IMPORTANT: Only generate full songs (JSON responses) when the user explicitly requests song creation with phrases like:
- "create a song"
- "make a song"
- "generate a song"
- "write a song"
- "I want a song about..."
- "create more versions"
- "make more"

If the user asks about something that's not song-related, engage them with follow-up questions to help them create a song.

When the user explicitly requests song creation, generate original song lyrics and a creative song title based on the topic.

IMPORTANT: Both songs should have the SAME base title, with version numbers in parentheses: "(#1)" and "(#2)".

TITLE LENGTH RULE: Keep song titles to a maximum of 2 words only.

LYRICS STRUCTURE RULE: All lyrics must follow this exact structure with 4 sections:
- Chorus (4-6 lines)
- Verse 1 (4-6 lines)
- Chorus (Repeat the exact same chorus)
- Verse 2 (4-6 lines)

You must respond with a JSON object containing:
1. "reply" - A friendly message
2. "lyrics" - A structured object with individual sections (null for more versions of existing songs)
3. "songs" - An array with 2 song objects with "title" and "genres"
4. "suggestions" - An array of suggestion pills

GENRES RULE: Each song should include "genres" as an array of 1-3 genre strings. BOTH songs MUST have the EXACT SAME genres array - they are variations of the same song with the same style.

Format:
{
  "reply": "",
  "lyrics": {
    "sections": [
      {"type": "chorus", "content": "..."},
      {"type": "verse1", "content": "..."},
      {"type": "chorus", "content": "..."},
      {"type": "verse2", "content": "..."}
    ]
  },
  "songs": [
    {"title": "Song Title (#1)", "genres": ["pop", "indie"]},
    {"title": "Song Title (#2)", "genres": ["pop", "indie"]}
  ],
  "suggestions": ["Extend", "Edit Lyrics", "Change Genre", "Replace Section"]
}`;
  }
}

// Export singleton instance
export default new GeminiChatService();
